import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Stack, Chip, Alert, Divider, CircularProgress, Grid, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, LinearProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Switch, FormControlLabel, Link,
} from '@mui/material';
import { CreditCard, CheckCircle, Lock, LocalOffer, AccountBalanceWallet, Receipt, Autorenew } from '@mui/icons-material';
import { api } from '../config/api';
import { useAuth } from '../context/AuthContext';

const clp = (n: number | null | undefined) => `$${Math.round(Number(n || 0)).toLocaleString('es-CL')}`;
const fecha = (d?: string | null) => (d ? new Date(d).toLocaleDateString('es-CL', { dateStyle: 'medium' }) : '—');

const ESTADO_PAGO: Record<string, { label: string; color: any }> = {
  pagado: { label: 'Pagado', color: 'success' },
  pendiente: { label: 'Pendiente', color: 'warning' },
  rechazado: { label: 'Rechazado', color: 'error' },
  expirado: { label: 'Expirado', color: 'default' },
  revision: { label: 'En revisión', color: 'warning' },
};
const TIPO_PAGO: Record<string, string> = {
  suscripcion: 'Suscripción', cambio_plan: 'Cambio de plan', renovacion_auto: 'Renovación automática', recarga_saldo: 'Recarga de saldo',
};
const MENSAJE_RETORNO: Record<string, { sev: any; txt: string }> = {
  pagado: { sev: 'success', txt: '¡Pago confirmado! Tu plan ya está activo.' },
  ya_procesado: { sev: 'success', txt: 'Este pago ya estaba confirmado.' },
  pendiente: { sev: 'info', txt: 'Tu pago está en proceso. Te avisaremos por correo cuando se confirme.' },
  rechazado: { sev: 'error', txt: 'El pago fue rechazado o anulado. Puedes intentarlo de nuevo.' },
  monto_distinto: { sev: 'warning', txt: 'Recibimos tu pago, pero lo estamos revisando. Te contactaremos.' },
  desconocido: { sev: 'warning', txt: 'No pudimos identificar el pago. Si se te cobró, contáctanos.' },
};

const Suscripcion: React.FC = () => {
  const { refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const [lic, setLic] = useState<any>(null);
  const [planes, setPlanes] = useState<any[]>([]);
  const [pagos, setPagos] = useState<any[]>([]);
  const [uso, setUso] = useState<any>(null);
  const [saldo, setSaldo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [cupon, setCupon] = useState('');
  const [cotizacion, setCotizacion] = useState<any>(null);
  const [planSel, setPlanSel] = useState<string | null>(null);
  const [recarga, setRecarga] = useState('10000');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [motivo, setMotivo] = useState('');

  const cargar = useCallback(async () => {
    const [e, p, h, u, s] = await Promise.all([
      api.get('/suscripcion/estado'),
      api.get('/suscripcion/planes'),
      api.get('/suscripcion/pagos'),
      api.get('/suscripcion/uso'),
      api.get('/suscripcion/saldo'),
    ]);
    setLic(e.data.licencia);
    setPlanes(p.data.planes || []);
    setPagos(h.data.pagos || []);
    setUso(u.data);
    setSaldo(s.data);
  }, []);

  useEffect(() => {
    cargar().catch(() => setError('No se pudo cargar tu suscripción')).finally(() => setLoading(false));
  }, [cargar]);

  // Mensajes al volver de Flow (?pago=... / ?tarjeta=...)
  useEffect(() => {
    const pago = params.get('pago');
    const tarjeta = params.get('tarjeta');
    if (pago && MENSAJE_RETORNO[pago]) {
      const m = MENSAJE_RETORNO[pago];
      if (m.sev === 'error' || m.sev === 'warning') setError(m.txt); else setOk(m.txt);
      refresh();
    }
    if (tarjeta) {
      if (tarjeta === 'ok') setOk('Tarjeta registrada. Tu plan se renovará automáticamente.'); else setError('No se pudo registrar la tarjeta.');
    }
    if (pago || tarjeta) { params.delete('pago'); params.delete('orden'); params.delete('tarjeta'); setParams(params, { replace: true }); }
  }, []); // solo al montar

  const elegirPlan = async (codigo: string, conCupon?: string) => {
    setPlanSel(codigo); setError('');
    try {
      // Si no se pasa explícitamente un cupón, usar el del state (que persiste entre planes)
      const cuponAUsar = conCupon !== undefined ? conCupon : cupon;
      const { data } = await api.post('/suscripcion/cotizar', { plan: codigo, cupon: cuponAUsar || null });
      setCotizacion(data.cotizacion);
      if (cuponAUsar && data.cotizacion.cuponError) setError(data.cotizacion.cuponError);
    } catch (e: any) {
      setError(e.response?.data?.error || 'No se pudo calcular el precio');
    }
  };

  const pagar = async () => {
    if (!planSel) return;
    setProcesando(true); setError('');
    try {
      const { data } = await api.post('/suscripcion/contratar', { plan: planSel, cupon: cotizacion?.cupon || null });
      if (data.paymentUrl) { window.location.href = data.paymentUrl; return; }
      setOk(data.mensaje || 'Listo');
      setCotizacion(null); setPlanSel(null);
      await cargar(); await refresh();
    } catch (e: any) {
      setError(e.response?.data?.error || 'No se pudo iniciar el pago.');
    }
    setProcesando(false);
  };

  const accion = async (fn: () => Promise<any>, exito: string) => {
    setError(''); setOk('');
    try { await fn(); setOk(exito); await cargar(); await refresh(); } catch (e: any) { setError(e.response?.data?.error || 'No se pudo completar la acción'); }
  };

  const recargar = async () => {
    setProcesando(true); setError('');
    try {
      const { data } = await api.post('/suscripcion/recargar', { monto: parseInt(recarga, 10) });
      window.location.href = data.paymentUrl;
    } catch (e: any) {
      setError(e.response?.data?.error || 'No se pudo iniciar la recarga');
      setProcesando(false);
    }
  };

  const registrarTarjeta = async () => {
    setProcesando(true); setError('');
    try {
      const { data } = await api.post('/suscripcion/tarjeta/registrar');
      window.location.href = data.redirectUrl;
    } catch (e: any) {
      setError(e.response?.data?.error || 'No se pudo iniciar el registro de tarjeta');
      setProcesando(false);
    }
  };

  if (loading) return <Box sx={{ textAlign: 'center', mt: 6 }}><CircularProgress /></Box>;

  const estado = lic?.estado;
  const vigente = !!lic?.vigente;
  const chip = estado === 'activa' ? { c: 'success', l: `${lic.plan.nombre} activo` }
    : estado === 'trial' ? { c: 'warning', l: `Prueba gratis (${lic.diasRestantes} días)` }
    : estado === 'gracia' ? { c: 'warning', l: `Pago pendiente (${lic.diasRestantes} días de gracia)` }
    : estado === 'cancelada' ? { c: 'error', l: 'Plan cancelado' }
    : { c: 'error', l: 'Sin plan vigente' };
  const limMsg = uso?.limites?.mensajes;
  const pctMsg = limMsg ? Math.min(100, Math.round((uso.uso.mensajes_bot / limMsg) * 100)) : 0;
  const pendiente = pagos.find((p) => p.estado === 'pendiente' && p.payment_url);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <CreditCard color="primary" />
        <Typography variant="h4">Suscripción</Typography>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>Tu plan, pagos, saldo y uso de tRespondo.cl.</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert>}
      {pendiente && (
        <Alert severity="info" sx={{ mb: 2 }} action={<Button color="inherit" size="small" href={pendiente.payment_url}>Retomar pago</Button>}>
          Tienes un pago pendiente de {clp(pendiente.monto)} ({pendiente.commerce_order}).
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Estado */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}><CardContent>
            <Typography variant="overline" color="text.secondary">Estado actual</Typography>
            <Box sx={{ my: 1 }}><Chip color={chip.c as any} label={chip.l} /></Box>
            <Typography variant="body2">Plan: <b>{lic?.plan?.nombre}</b> · {clp(lic?.plan?.precio_clp)} / {lic?.plan?.periodo_dias} días</Typography>
            {lic?.fin && <Typography variant="body2">{estado === 'trial' ? 'La prueba termina' : vigente ? 'Vigente hasta' : 'Terminó'} el {fecha(lic.fin)}</Typography>}
            {lic?.planSiguiente && (
              <Alert severity="info" sx={{ mt: 1 }} action={<Button size="small" color="inherit" onClick={() => accion(() => api.post('/suscripcion/cancelar-cambio-plan'), 'Cambio de plan anulado')}>Anular</Button>}>
                Desde la próxima renovación pasarás al plan {planes.find((p) => p.codigo === lic.planSiguiente)?.nombre || lic.planSiguiente}.
              </Alert>
            )}
            {lic?.cancelarAlFin ? (
              <Alert severity="warning" sx={{ mt: 1 }} action={<Button size="small" color="inherit" onClick={() => accion(() => api.post('/suscripcion/reanudar'), 'Tu plan seguirá renovándose')}>Reanudar</Button>}>
                Cancelaste tu plan. Termina el {fecha(lic.fin)}.
              </Alert>
            ) : (estado === 'activa' || estado === 'trial') && (
              <Button size="small" color="error" sx={{ mt: 1 }} onClick={() => setCancelOpen(true)}>Cancelar plan</Button>
            )}
          </CardContent></Card>
        </Grid>

        {/* Uso del mes */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}><CardContent>
            <Typography variant="overline" color="text.secondary">Uso del mes ({uso?.uso?.periodo})</Typography>
            <Typography variant="h5" sx={{ mt: 1 }}>{(uso?.uso?.mensajes_bot || 0).toLocaleString('es-CL')} respuestas</Typography>
            {limMsg ? (
              <>
                <LinearProgress variant="determinate" value={pctMsg} color={pctMsg >= 90 ? 'error' : 'primary'} sx={{ my: 1, height: 8, borderRadius: 4 }} />
                <Typography variant="caption" color="text.secondary">
                  {pctMsg}% de {limMsg.toLocaleString('es-CL')} incluidas
                  {uso.limites.precioExtra > 0 ? ` · luego ${clp(uso.limites.precioExtra)} c/u desde tu saldo` : ' · al llegar al límite el bot se detiene'}
                </Typography>
              </>
            ) : <Typography variant="caption" color="text.secondary">Respuestas ilimitadas en tu plan</Typography>}
            <Typography variant="body2" sx={{ mt: 1 }}>
              IA: {((uso?.uso?.tokens_entrada || 0) + (uso?.uso?.tokens_salida || 0)).toLocaleString('es-CL')} tokens
              {uso?.limites?.tokens ? ` de ${Number(uso.limites.tokens).toLocaleString('es-CL')}` : ''}
            </Typography>
            {uso?.uso?.mensajes_extra > 0 && <Typography variant="body2">Respuestas extra cobradas: {uso.uso.mensajes_extra}</Typography>}
          </CardContent></Card>
        </Grid>

        {/* Saldo */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}><CardContent>
            <Stack direction="row" spacing={1} alignItems="center"><AccountBalanceWallet color="primary" fontSize="small" /><Typography variant="overline" color="text.secondary">Saldo prepago</Typography></Stack>
            <Typography variant="h5" sx={{ my: 1 }}>{clp(saldo?.saldo)}</Typography>
            <Stack direction="row" spacing={1}>
              <TextField size="small" type="number" label="Monto" value={recarga} onChange={(e) => setRecarga(e.target.value)} inputProps={{ min: saldo?.recargaMin, step: 1000 }} sx={{ width: 140 }} />
              <Button variant="outlined" onClick={recargar} disabled={procesando}>Recargar</Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">Se usa para respuestas sobre el límite del plan. Mínimo {clp(saldo?.recargaMin)}.</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* Planes */}
      <Typography variant="h6" sx={{ mb: 1 }}>{vigente && estado === 'activa' ? 'Renovar o cambiar de plan' : 'Elige tu plan'}</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {planes.map((p) => {
          const actual = p.codigo === lic?.plan?.codigo;
          return (
            <Grid item xs={12} md={Math.max(4, 12 / Math.max(1, planes.length))} key={p.codigo}>
              <Card sx={{ border: '2px solid', borderColor: planSel === p.codigo ? 'primary.main' : actual ? 'success.light' : '#E2E8F0', height: '100%' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" color="primary">{p.nombre}</Typography>
                    {actual && <Chip size="small" color="success" label="Tu plan" />}
                  </Stack>
                  <Typography variant="h4" sx={{ fontWeight: 800, my: 1 }}>
                    {clp(p.precio_clp)}<Typography component="span" variant="body1" color="text.secondary"> / {p.periodo_dias === 30 ? 'mes' : `${p.periodo_dias} días`}</Typography>
                  </Typography>
                  {p.descripcion && <Typography variant="body2" color="text.secondary">{p.descripcion}</Typography>}
                  <Divider sx={{ my: 1.5 }} />
                  <Stack spacing={0.8} sx={{ mb: 2 }}>
                    {(p.features || []).map((f: string) => (
                      <Stack key={f} direction="row" spacing={1} alignItems="center"><CheckCircle color="success" fontSize="small" /><Typography variant="body2">{f}</Typography></Stack>
                    ))}
                    {p.limite_mensajes_mes && <Typography variant="body2">• {Number(p.limite_mensajes_mes).toLocaleString('es-CL')} respuestas/mes</Typography>}
                  </Stack>
                  <Button fullWidth variant={planSel === p.codigo ? 'contained' : 'outlined'} onClick={() => elegirPlan(p.codigo)}>
                    {actual ? (estado === 'activa' ? 'Renovar' : 'Pagar este plan') : estado === 'activa' ? 'Cambiar a este plan' : 'Elegir'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {cotizacion && (
        <Card sx={{ mb: 3, borderLeft: '4px solid', borderColor: 'primary.main' }}><CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{cotizacion.descripcion}</Typography>
          {!cotizacion.programado && (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ my: 1.5 }}>
                <TextField size="small" label="Cupón de descuento" value={cupon} onChange={(e) => setCupon(e.target.value.toUpperCase())}
                  InputProps={{ startAdornment: <LocalOffer fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }} />
                <Button onClick={() => planSel && elegirPlan(planSel, cupon)} disabled={!cupon || !planSel}>Aplicar</Button>
                {cotizacion.cupon && <Chip color="success" label={`${cotizacion.cupon}: -${clp(cotizacion.descuento)}`} onDelete={() => { setCupon(''); planSel && elegirPlan(planSel, ''); }} />}
              </Stack>
              {cotizacion.credito > 0 && <Typography variant="body2">Crédito por días no usados: -{clp(cotizacion.credito)}</Typography>}
              <Typography variant="h5" sx={{ fontWeight: 800 }}>Total: {clp(cotizacion.monto)}</Typography>
            </>
          )}
          {cotizacion.programado && <Typography variant="body2" sx={{ my: 1 }}>No se cobra nada ahora. Mantienes tu plan actual hasta el {fecha(lic?.fin)}.</Typography>}
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            <Button variant="contained" size="large" onClick={pagar} disabled={procesando}>
              {procesando ? 'Procesando...' : cotizacion.programado ? 'Programar cambio' : cotizacion.monto > 0 ? 'Pagar con Flow' : 'Activar'}
            </Button>
            <Button onClick={() => { setCotizacion(null); setPlanSel(null); }}>Cancelar</Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <Lock fontSize="inherit" /> Pago seguro con Flow
          </Typography>
        </CardContent></Card>
      )}

      {/* Cobro automático */}
      <Card sx={{ mb: 3 }}><CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center"><Autorenew color="primary" /><Typography variant="h6">Renovación automática</Typography></Stack>
            <Typography variant="body2" color="text.secondary">
              {lic?.tarjeta ? `Tarjeta ${lic.tarjeta.tipo || ''} terminada en ${lic.tarjeta.ultimos4}.` : 'Registra una tarjeta en Flow para que tu plan se renueve solo al vencer.'}
              {' '}Si un cobro falla, reintentamos hasta 3 veces y te avisamos por correo.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {lic?.tarjeta && (
              <FormControlLabel
                control={<Switch checked={!!lic.cobroAutomatico} onChange={(e) => accion(() => api.post('/suscripcion/cobro-automatico', { activo: e.target.checked }), e.target.checked ? 'Renovación automática activada' : 'Renovación automática desactivada')} />}
                label="Activa"
              />
            )}
            <Button variant="outlined" onClick={registrarTarjeta} disabled={procesando}>{lic?.tarjeta ? 'Cambiar tarjeta' : 'Registrar tarjeta'}</Button>
            {lic?.tarjeta && <Button color="error" onClick={() => accion(() => api.delete('/suscripcion/tarjeta'), 'Tarjeta eliminada')}>Quitar</Button>}
          </Stack>
        </Stack>
      </CardContent></Card>

      {/* Historial */}
      <Card><CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}><Receipt color="primary" /><Typography variant="h6">Historial de pagos</Typography></Stack>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Fecha</TableCell><TableCell>Concepto</TableCell><TableCell>Orden</TableCell>
              <TableCell align="right">Monto</TableCell><TableCell>Estado</TableCell><TableCell>Comprobante</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {pagos.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{fecha(p.pagado_at || p.created_at)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{TIPO_PAGO[p.tipo] || p.tipo}</Typography>
                    {p.cupon_codigo && <Typography variant="caption" color="text.secondary">Cupón {p.cupon_codigo} (-{clp(p.descuento)})</Typography>}
                  </TableCell>
                  <TableCell><Typography variant="caption">{p.commerce_order}</Typography></TableCell>
                  <TableCell align="right">{clp(p.monto)}</TableCell>
                  <TableCell>
                    <Chip size="small" color={ESTADO_PAGO[p.estado]?.color || 'default'} label={ESTADO_PAGO[p.estado]?.label || p.estado} />
                    {p.reembolso_estado && <Chip size="small" sx={{ ml: 0.5 }} label={`Reembolso ${p.reembolso_estado}`} />}
                  </TableCell>
                  <TableCell>
                    {p.boleta_url ? <Link href={p.boleta_url} target="_blank" rel="noopener noreferrer">Boleta {p.boleta_folio || ''}</Link>
                      : p.boleta_folio ? `Boleta ${p.boleta_folio}`
                      : p.comprobante_numero ? `N° ${p.comprobante_numero}` : '—'}
                  </TableCell>
                </TableRow>
              ))}
              {pagos.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>Aún no tienes pagos</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent></Card>

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Cancelar plan</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Tu agente seguirá respondiendo hasta el {fecha(lic?.fin)}. Después no se renovará. Puedes reanudarlo antes de esa fecha.
          </Typography>
          <TextField fullWidth multiline minRows={2} label="¿Por qué cancelas? (opcional)" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Volver</Button>
          <Button color="error" variant="contained" onClick={() => { setCancelOpen(false); accion(() => api.post('/suscripcion/cancelar', { motivo }), 'Cancelación programada'); }}>Confirmar cancelación</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Suscripcion;
